import { Node } from '@tiptap/core';
import { blockNodeSpec, dataAttribute } from './block';
import { blockViews, initialView } from './block_views';
import { parseTimestamp, formatTimestamp } from './quote';

// Audio clip: a start and an end (whole seconds) into the post's episode and an optional caption. Saved as a
// bare block placeholder with data-clip-start / -end / -caption; Newsletters::RenderBlocks renders it, and the
// episode is always the post's own, never an attribute. Nothing is trimmed: the clip is just two times. The
// editor chrome lives in dashboard/shared/tiptap/_clip_editor.html.erb and its preview (plus the transcript
// excerpt at start-end, when the episode has one) comes from the server. The block shows one view at a time
// (block_views.js): the key moments, the editor, or the preview. A clip with a valid range opens in its
// preview, a new one in the key moments when there are any, else in the editor.
//
// The page passes data-tiptap-block-capabilities-value.clip = { post_id, episode, episode_url, duration,
// max_length, default_length, suggestions }. `episode` turns the block on (a clip needs one, and the slash
// item is hidden without it); `suggestions` (the transcript's key moments, only present when AI blocks are on
// and the transcript has been read) fills the purple "Key moments" box.
//
// The rules mirror Block::Newsletter::ClipComponent.range_error, which is what actually decides at render:
// 0 <= start < end, end - start <= max_length, end <= duration when it is known.

const PREVIEW_DELAY_MS = 300;

// "21s", or "1m 05s" from a minute up. Mirrors ClipComponent#length_label.
export function formatLength(seconds) {
  const total = Math.floor(Number(seconds));
  if (!Number.isFinite(total) || total < 0) return '';
  const minutes = Math.floor(total / 60);
  return minutes > 0 ? `${minutes}m ${String(total % 60).padStart(2, '0')}s` : `${total}s`;
}

// The reason a start/end pair can't be a clip, or null. Either may be null (not entered yet).
export function rangeError(start, end, { duration, maxLength } = {}) {
  if (start === null || end === null) return null;
  if (end <= start) return 'The end has to come after the start.';
  if (maxLength && end - start > maxLength) return `A clip can be up to ${formatLength(maxLength)} long.`;
  if (duration && end > duration) return `The episode is only ${formatTimestamp(duration)} long.`;
  return null;
}

// The start and end "Trim to this" gives a key moment. A moment with no end (or one not after the start) runs
// default_length seconds; an end past the episode, or a clip over max_length, is pulled back in.
export function trimRange(moment, { duration, maxLength, defaultLength }) {
  if (moment.start == null) return null;
  const start = Math.floor(Number(moment.start));
  if (!Number.isFinite(start) || start < 0) return null;

  let end = moment.end == null ? NaN : Math.floor(Number(moment.end));
  if (!Number.isFinite(end) || end <= start) end = start + (defaultLength || 30);
  if (maxLength) end = Math.min(end, start + maxLength);
  if (duration) end = Math.min(end, duration);
  return end > start ? { start, end } : null;
}

const ATTRS = { start: 'clipStart', end: 'clipEnd' };

export const Clip = Node.create({
  name: 'clip',

  ...blockNodeSpec('clip', {
    attributes: {
      clipStart: dataAttribute('clipStart'),
      clipEnd: dataAttribute('clipEnd'),
      clipCaption: dataAttribute('clipCaption'),
    },
  }),

  addOptions() {
    return {
      templates: null,         // { editor_html } — injected by the tiptap controller from the server template
      capabilities: null,      // { post_id, episode, episode_url, duration, max_length, default_length, suggestions }
      fetchRenderedHtml: null, // async (attrs) => string | null — injected by the tiptap controller
      onRenderedHtmlFetched: null,
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const extension = this;
      const capabilities = extension.options.capabilities || {};
      const limits = {
        duration: capabilities.duration,
        maxLength: capabilities.max_length,
        defaultLength: capabilities.default_length,
      };
      let currentNode = node;
      let previewTimer = null;
      let previewRequest = 0;

      const dom = document.createElement('div');
      dom.setAttribute('data-clip-node', '');
      dom.innerHTML = extension.options.templates?.editor_html
        || '<div class="my-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500">Audio clip</div>';

      const field = name => dom.querySelector(`[data-field="${name}"]`);
      const preview = dom.querySelector('[data-preview]');
      const hint = dom.querySelector('[data-preview-hint]');
      const errorEl = dom.querySelector('[data-error]');
      const lengthEl = dom.querySelector('[data-length]');
      const checkLink = dom.querySelector('[data-check]');

      const setAttrs = attrs => {
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...currentNode.attrs, ...attrs }));
      };

      const seconds = name => parseTimestamp(currentNode.attrs[ATTRS[name]]);

      // Names of the time fields holding text that isn't a time; syncFromNode leaves those inputs alone.
      const unparsable = new Set();
      let message = '';

      const showError = text => {
        message = text || '';
        if (!errorEl) return;
        errorEl.textContent = message;
        errorEl.classList.toggle('hidden', !message);
      };

      const refreshStatus = () => {
        const start = seconds('start');
        const end = seconds('end');
        const problem = unparsable.size > 0 ? 'Use minutes and seconds, like 32:10.' : rangeError(start, end, limits);
        showError(problem);
        views?.refresh();
        if (lengthEl) lengthEl.textContent = start !== null && end !== null && end > start ? formatLength(end - start) : '';

        if (checkLink) {
          if (!capabilities.episode_url || start === null) {
            checkLink.classList.add('hidden');
          } else {
            // Preview clip: the episode page at the clip's start, opened in a new tab. It plays from the
            // start but does not stop at the end (only the post's own clip card does that).
            const url = new URL(capabilities.episode_url, window.location.origin);
            url.searchParams.set('timestamp', start);
            checkLink.setAttribute('href', url.toString());
            checkLink.classList.remove('hidden');
          }
        }
      };

      const syncFromNode = () => {
        Object.keys(ATTRS).forEach(name => {
          const el = field(name);
          if (el && el !== document.activeElement && !unparsable.has(name)) {
            const value = seconds(name);
            el.value = value === null ? '' : formatTimestamp(value);
          }
        });
        const caption = field('caption');
        if (caption && caption !== document.activeElement) caption.value = currentNode.attrs.clipCaption ?? '';
        refreshStatus();
        views?.refresh();
      };

      const showPreview = html => {
        if (!preview) return;
        preview.innerHTML = html || '';
        hint?.classList.toggle('hidden', Boolean(html));
      };

      const schedulePreview = () => {
        if (!extension.options.fetchRenderedHtml || views?.view !== 'preview') return;
        clearTimeout(previewTimer);
        previewTimer = setTimeout(async () => {
          const request = ++previewRequest;
          const html = await extension.options.fetchRenderedHtml({ ...currentNode.attrs, postId: capabilities.post_id || '' });
          if (request === previewRequest) showPreview(html);
        }, PREVIEW_DELAY_MS);
      };

      // A time is typed as mm:ss or h:mm:ss and saved as seconds. Text that isn't a time is flagged and not
      // saved. A pair that doesn't make a clip is flagged too but saved as typed: the end is usually typed
      // after a start that is still later than the old end, and the server renders nothing for it anyway.
      Object.keys(ATTRS).forEach(name => {
        field(name)?.addEventListener('input', e => {
          const text = e.target.value.trim();
          const parsed = parseTimestamp(text);
          if (text !== '' && parsed === null) {
            unparsable.add(name);
            setAttrs({ [ATTRS[name]]: '' });
          } else {
            unparsable.delete(name);
            setAttrs({ [ATTRS[name]]: parsed === null ? '' : String(parsed) });
          }
          refreshStatus();
        });
      });

      // ±1s nudges the field's time and keeps it inside the episode.
      dom.querySelectorAll('[data-nudge]').forEach(button => {
        button.addEventListener('click', () => {
          const name = button.dataset.nudge;
          const step = parseInt(button.dataset.step, 10);
          let value = (unparsable.has(name) ? null : seconds(name)) ?? 0;
          value = Math.max(0, value + step);
          if (limits.duration) value = Math.min(value, limits.duration);
          unparsable.delete(name);
          const el = field(name);
          if (el) el.value = formatTimestamp(value);
          setAttrs({ [ATTRS[name]]: String(value) });
          refreshStatus();
        });
      });

      field('caption')?.addEventListener('input', e => setAttrs({ clipCaption: e.target.value }));

      // Without an episode there is nothing to cut a clip from. With key moments the block opens on them:
      // "Trim to this" fills the block and opens the editor, "Custom clip" opens it as is. The Done button
      // needs a start and an end that make a clip.
      const ai = dom.querySelector('[data-ai]');
      const suggestions = Array.isArray(capabilities.suggestions) ? capabilities.suggestions : [];
      const hasSuggestions = Boolean(capabilities.episode) && suggestions.length > 0;
      const isValidClip = () => {
        const start = seconds('start');
        const end = seconds('end');
        return capabilities.episode && unparsable.size === 0 && start !== null && end !== null && rangeError(start, end, limits) === null;
      };
      const views = blockViews(dom, {
        canFinish: isValidClip,
        hasSuggestions: () => hasSuggestions,
        onChange: view => { if (view === 'preview') schedulePreview(); },
      });

      if (!capabilities.episode) {
        dom.querySelector('[data-no-episode]')?.classList.remove('hidden');
        dom.querySelector('[data-fields]')?.classList.add('hidden');
      }

      const rowTemplate = dom.querySelector('template[data-suggestion-row]');
      if (hasSuggestions && ai && rowTemplate) {
        const list = ai.querySelector('[data-suggestions]');

        suggestions.forEach(moment => {
          const range = trimRange(moment, limits);
          if (!range) return;

          const row = rowTemplate.content.firstElementChild.cloneNode(true);
          row.querySelector('[data-suggestion-title]').textContent = moment.title || '';
          row.querySelector('[data-suggestion-meta]').textContent =
            `${formatTimestamp(range.start)} · ${formatLength(range.end - range.start)}`;
          row.querySelector('[data-use]').addEventListener('click', () => {
            unparsable.clear();
            setAttrs({ clipStart: String(range.start), clipEnd: String(range.end) });
            views.showEditor();
          });
          list.appendChild(row);
        });
      }

      dom.querySelector('[data-remove]')?.addEventListener('mousedown', e => {
        e.preventDefault();
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        const current = editor.state.doc.nodeAt(pos);
        if (current) editor.chain().focus().deleteRange({ from: pos, to: pos + current.nodeSize }).run();
      });

      // The preview is made of real links; never navigate away from the editor.
      preview?.addEventListener('click', e => e.preventDefault());

      syncFromNode();
      views.setView(initialView({ hasContent: Boolean(isValidClip()), hasSuggestions }));

      return {
        dom,
        // Typing in the fields must not reach ProseMirror (Backspace/Enter would act on the node).
        stopEvent: event => Boolean(event.target?.closest?.('input, textarea, button, a')),
        ignoreMutation: () => true,
        update(updatedNode) {
          if (updatedNode.type.name !== 'clip') return false;
          currentNode = updatedNode;
          syncFromNode();
          schedulePreview();
          return true;
        },
        destroy() {
          clearTimeout(previewTimer);
        },
      };
    };
  },

  addCommands() {
    return {
      insertClip: (attrs = {}) => ({ chain }) =>
        chain().insertContent({ type: this.name, attrs }).run(),
    };
  },
});

function clipAvailable(editor) {
  const extension = editor.extensionManager.extensions.find(ext => ext.name === 'clip');
  return Boolean(extension?.options.capabilities?.episode);
}

// Only offered when the post has an episode: a clip is a moment of it. Unlike chapters, a post can hold
// several clips.
export const clipSlashItem = {
  title: 'Audio clip',
  description: 'A short moment of the episode, with a play button and progress',
  icon: '▶',
  group: 'Blocks',
  keywords: ['clip', 'audio', 'moment', 'highlight', 'episode', 'snippet'],
  canInsert: editor => clipAvailable(editor),
  command: ({ editor, range }) => {
    editor.chain().focus().deleteRange(range).insertClip().run();
  },
};
